import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { dashboardAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from 'recharts';

const fmt = (n) => n != null ? new Intl.NumberFormat('vi-VN').format(n) : '—';
const fmtM = (n) => n ? `${(n / 1e6).toFixed(1)}M` : '—';
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('vi-VN', { day:'2-digit', month:'2-digit' }) : '—';

const LEAVE_TYPE = {
  annual:'Năm', sick:'Ốm', maternity:'Thai sản', paternity:'Thai sản',
  marriage:'Kết hôn', funeral:'Tang', unpaid:'KHL', compensatory:'Bù',
};
const CAMPAIGN_TYPE = {
  recruitment:'Tuyển dụng', training:'Đào tạo', engagement:'Gắn kết',
  communication:'Truyền thông', other:'Khác',
};
const CAMPAIGN_COLOR = {
  recruitment:'#2563eb', training:'#7c3aed', engagement:'#059669',
  communication:'#d97706', other:'#64748b',
};

function StatCard({ icon, label, value, sub, color, to, onClick }) {
  const style = {
    background: '#fff', borderRadius: 14, padding: '18px 22px',
    boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderLeft: `4px solid ${color}`,
    flex: 1, minWidth: 160, cursor: to || onClick ? 'pointer' : 'default',
    transition: 'box-shadow 0.15s, transform 0.15s',
  };
  return (
    <div style={style} onClick={onClick}
      onMouseEnter={e => { if (to || onClick) { e.currentTarget.style.boxShadow='0 6px 18px rgba(0,0,0,0.12)'; e.currentTarget.style.transform='translateY(-2px)'; }}}
      onMouseLeave={e => { e.currentTarget.style.boxShadow='0 1px 4px rgba(0,0,0,0.08)'; e.currentTarget.style.transform=''; }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
        <span style={{ fontSize:26 }}>{icon}</span>
        {(to || onClick) && <span style={{ fontSize:11, color:'#94a3b8' }}>→</span>}
      </div>
      <div style={{ fontSize:30, fontWeight:800, color:'#1e293b', margin:'6px 0 2px' }}>{value}</div>
      <div style={{ fontSize:13, color:'#64748b', fontWeight:500 }}>{label}</div>
      {sub && <div style={{ fontSize:11, color, marginTop:3, fontWeight:600 }}>{sub}</div>}
    </div>
  );
}

function Section({ title, children, action }) {
  return (
    <div style={{ background:'#fff', borderRadius:14, padding:'18px 20px', boxShadow:'0 1px 4px rgba(0,0,0,0.08)' }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
        <h3 style={{ margin:0, fontSize:15, fontWeight:700, color:'#1e293b' }}>{title}</h3>
        {action}
      </div>
      {children}
    </div>
  );
}

export default function DashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const navigate = useNavigate();

  const isManager = ['admin','hr_manager','manager','hr_staff'].includes(user?.role);
  const isAdmin   = ['admin','hr_manager'].includes(user?.role);

  useEffect(() => {
    dashboardAPI.get()
      .then(r => setData(r.data.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'60vh', color:'#64748b', fontSize:14 }}>
      Đang tải dữ liệu...
    </div>
  );

  const emp      = data?.employees || {};
  const att      = data?.attendance_today || {};
  const payroll  = data?.latest_payroll;
  const tasks    = data?.tasks_today || {};
  const camps    = data?.campaigns || {};
  const taskPct  = tasks.total > 0 ? Math.round((tasks.done / tasks.total) * 100) : 0;

  // Chart data
  const empByDept = (data?.emp_by_dept || []).map(d => ({ name: d.name?.split(' ')[0], count: Number(d.count) }));
  const statusData = [
    { name: 'Active',    value: Number(emp.active    || 0), color:'#22c55e' },
    { name: 'Thử việc', value: Number(emp.probation  || 0), color:'#f59e0b' },
    { name: 'Đã nghỉ',  value: Number(emp.resigned   || 0), color:'#ef4444' },
    { name: 'Inactive', value: Number(emp.inactive   || 0), color:'#94a3b8' },
  ].filter(d => d.value > 0);

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:20 }}>

      {/* Greeting */}
      <div>
        <h1 style={{ fontSize:22, fontWeight:700, color:'#1e293b', margin:0 }}>
          Chào mừng, {user?.name} 👋
        </h1>
        <p style={{ color:'#64748b', margin:'4px 0 0', fontSize:13 }}>
          {new Date().toLocaleDateString('vi-VN', { weekday:'long', year:'numeric', month:'long', day:'numeric' })}
        </p>
      </div>

      {/* ── ROW 1: Core stats ── */}
      <div style={{ display:'flex', gap:14, flexWrap:'wrap' }}>
        <StatCard icon="👥" label="Tổng nhân viên" value={emp.total || '—'}
          sub={`${emp.active || 0} đang làm việc`} color="#1e3a8a"
          onClick={() => navigate('/employees')} />
        <StatCard icon="⏱️" label="Chấm công hôm nay" value={att.checked_in || 0}
          sub={att.late_count > 0 ? `${att.late_count} đi muộn` : 'Đúng giờ'}
          color="#0891b2" onClick={() => navigate('/attendance')} />
        <StatCard icon="📅" label="Đơn nghỉ chờ duyệt" value={data?.pending_leave || 0}
          sub="Cần xử lý" color="#f59e0b" onClick={() => navigate('/leave')} />
        <StatCard icon="💰" label="Quỹ lương gần nhất"
          value={payroll ? fmtM(payroll.total_net) : '—'}
          sub={payroll ? `${payroll.period} · ${payroll.employee_count} NV` : 'Chưa có'}
          color="#22c55e" onClick={() => isAdmin && navigate('/payroll')} />
      </div>

      {/* ── ROW 2: Module stats ── */}
      <div style={{ display:'flex', gap:14, flexWrap:'wrap' }}>
        <StatCard icon="🏬" label="Phòng ban" value={data?.departments?.total || '—'}
          sub="Đơn vị tổ chức" color="#6366f1" onClick={() => navigate('/departments')} />
        <StatCard icon="💼" label="Chức danh" value={data?.positions?.total || '—'}
          sub="Vị trí công việc" color="#8b5cf6" onClick={() => navigate('/positions')} />
        <StatCard icon="🎯" label="Chiến dịch đang chạy" value={camps.active || 0}
          sub={`${camps.total || 0} tổng · ${camps.completed || 0} hoàn thành`}
          color="#0891b2" onClick={() => navigate('/campaigns')} />
        <StatCard icon="📋" label="Nhiệm vụ hôm nay"
          value={`${tasks.done || 0}/${tasks.total || 0}`}
          sub={tasks.total > 0 ? `${taskPct}% hoàn thành` : 'Chưa có nhiệm vụ'}
          color={taskPct === 100 ? '#16a34a' : taskPct >= 50 ? '#d97706' : '#64748b'}
          onClick={() => navigate('/tasks')} />
      </div>

      {/* ── ROW 3: Charts ── */}
      <div style={{ display:'grid', gridTemplateColumns:'1.4fr 1fr', gap:16 }}>

        {/* Bar chart: emp by dept */}
        <Section title="👥 Nhân viên theo phòng ban">
          {empByDept.length === 0 ? (
            <div style={{ color:'#94a3b8', fontSize:13, padding:'20px 0' }}>Không có dữ liệu</div>
          ) : (
            <ResponsiveContainer width="100%" height={190}>
              <BarChart data={empByDept} margin={{ top:4, right:8, left:-18, bottom:0 }}>
                <XAxis dataKey="name" fontSize={11} tick={{ fill:'#64748b' }} />
                <YAxis fontSize={11} tick={{ fill:'#64748b' }} allowDecimals={false} />
                <Tooltip formatter={(v) => [v, 'Nhân viên']} />
                <Bar dataKey="count" fill="#6366f1" radius={[5,5,0,0]}
                  label={{ position:'top', fontSize:11, fill:'#6366f1', fontWeight:700 }} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Section>

        {/* Pie chart: emp status */}
        <Section title="📊 Trạng thái nhân viên">
          {statusData.length === 0 ? (
            <div style={{ color:'#94a3b8', fontSize:13, padding:'20px 0' }}>Không có dữ liệu</div>
          ) : (
            <div style={{ display:'flex', alignItems:'center', gap:12 }}>
              <ResponsiveContainer width={150} height={150}>
                <PieChart>
                  <Pie data={statusData} cx="50%" cy="50%" innerRadius={42} outerRadius={65} dataKey="value" paddingAngle={2}>
                    {statusData.map((e,i) => <Cell key={i} fill={e.color} />)}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
              <div style={{ display:'flex', flexDirection:'column', gap:8, flex:1 }}>
                {statusData.map(s => (
                  <div key={s.name} style={{ display:'flex', alignItems:'center', gap:8 }}>
                    <div style={{ width:10, height:10, borderRadius:2, background:s.color, flexShrink:0 }} />
                    <span style={{ fontSize:12, color:'#64748b', flex:1 }}>{s.name}</span>
                    <strong style={{ fontSize:13, color:'#1e293b' }}>{s.value}</strong>
                  </div>
                ))}
                <div style={{ borderTop:'1px solid #f1f5f9', paddingTop:6, display:'flex', justifyContent:'space-between' }}>
                  <span style={{ fontSize:12, color:'#94a3b8' }}>Tổng</span>
                  <strong style={{ fontSize:13 }}>{emp.total || 0}</strong>
                </div>
              </div>
            </div>
          )}
        </Section>
      </div>

      {/* ── ROW 4: Tasks progress + Campaigns ── */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>

        {/* Daily task progress */}
        <Section title="📋 Tiến độ nhiệm vụ hôm nay"
          action={<a href="/tasks" onClick={e=>{e.preventDefault();navigate('/tasks');}} style={{ fontSize:12, color:'#6366f1', textDecoration:'none' }}>Xem tất cả →</a>}>
          {/* Overall bar */}
          <div style={{ marginBottom:14 }}>
            <div style={{ display:'flex', justifyContent:'space-between', fontSize:12, marginBottom:4 }}>
              <span style={{ color:'#64748b' }}>Toàn công ty</span>
              <span style={{ fontWeight:700, color: taskPct===100?'#16a34a':taskPct>=50?'#d97706':'#64748b' }}>
                {tasks.done||0}/{tasks.total||0} ({taskPct}%)
              </span>
            </div>
            <div style={{ background:'#f1f5f9', borderRadius:6, height:8, overflow:'hidden' }}>
              <div style={{ width:`${taskPct}%`, height:'100%', borderRadius:6, transition:'width 0.5s',
                background: taskPct===100?'#16a34a':taskPct>=50?'#d97706':'#6366f1' }} />
            </div>
          </div>
          {/* Per-employee rows */}
          {(data?.task_by_employee || []).length === 0 ? (
            <div style={{ color:'#94a3b8', fontSize:13 }}>Không có dữ liệu</div>
          ) : (
            <div style={{ display:'flex', flexDirection:'column', gap:7 }}>
              {data.task_by_employee.map((e, i) => {
                const pct = e.total > 0 ? Math.round((e.done / e.total) * 100) : 0;
                const col = pct===100?'#16a34a':pct>=50?'#d97706':'#6366f1';
                return (
                  <div key={i}>
                    <div style={{ display:'flex', justifyContent:'space-between', fontSize:11, marginBottom:2 }}>
                      <span style={{ color:'#475569', fontWeight:500 }}>{e.name}</span>
                      <span style={{ color:col, fontWeight:700 }}>{e.done}/{e.total}</span>
                    </div>
                    <div style={{ background:'#f1f5f9', borderRadius:4, height:5, overflow:'hidden' }}>
                      <div style={{ width:`${pct}%`, height:'100%', background:col, borderRadius:4, transition:'width 0.4s' }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Section>

        {/* Active campaigns */}
        <Section title="🎯 Chiến dịch đang triển khai"
          action={<a href="/campaigns" onClick={e=>{e.preventDefault();navigate('/campaigns');}} style={{ fontSize:12, color:'#6366f1', textDecoration:'none' }}>Xem tất cả →</a>}>
          {(data?.active_campaigns || []).length === 0 ? (
            <div style={{ color:'#94a3b8', fontSize:13 }}>Không có chiến dịch đang chạy</div>
          ) : (
            <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
              {data.active_campaigns.map(c => {
                const typeColor = CAMPAIGN_COLOR[c.type] || '#64748b';
                const daysLeft = c.end_date ? Math.ceil((new Date(c.end_date) - new Date()) / 86400000) : null;
                return (
                  <div key={c.id} style={{ display:'flex', alignItems:'flex-start', gap:10, padding:'9px 12px',
                    borderRadius:9, background:'#f8fafc', border:`1px solid #f1f5f9` }}>
                    <div style={{ width:4, borderRadius:2, alignSelf:'stretch', background:typeColor, flexShrink:0 }} />
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontSize:13, fontWeight:600, color:'#1e293b', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{c.title}</div>
                      <div style={{ fontSize:11, color:'#94a3b8', marginTop:2 }}>
                        {CAMPAIGN_TYPE[c.type] || c.type}
                        {c.department_name ? ` · ${c.department_name}` : ' · Toàn công ty'}
                      </div>
                    </div>
                    {daysLeft !== null && (
                      <span style={{ fontSize:10, fontWeight:700, padding:'2px 7px', borderRadius:10, flexShrink:0,
                        color: daysLeft<0?'#dc2626':daysLeft<=7?'#d97706':'#16a34a',
                        background: daysLeft<0?'#fee2e2':daysLeft<=7?'#fef3c7':'#dcfce7' }}>
                        {daysLeft<0?`Quá ${Math.abs(daysLeft)}n`:daysLeft===0?'Hôm nay':`${daysLeft}n`}
                      </span>
                    )}
                  </div>
                );
              })}
              {/* Campaign type overview */}
              <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginTop:4 }}>
                {[['active','Đang chạy','#0891b2','#cffafe'],['planning','Kế hoạch','#64748b','#f1f5f9'],['completed','Xong','#16a34a','#dcfce7']].map(([k,lbl,color,bg]) => (
                  camps[k] > 0 &&
                  <span key={k} style={{ fontSize:10, fontWeight:700, padding:'2px 8px', borderRadius:10, color, background:bg }}>
                    {lbl}: {camps[k]}
                  </span>
                ))}
              </div>
            </div>
          )}
        </Section>
      </div>

      {/* ── ROW 5: Pending leave table ── */}
      <Section title={`📅 Đơn nghỉ phép chờ duyệt (${data?.pending_leave || 0})`}
        action={
          <a href="/leave" onClick={e=>{e.preventDefault();navigate('/leave');}} style={{ fontSize:12, color:'#6366f1', textDecoration:'none' }}>
            Xem tất cả →
          </a>
        }>
        {(data?.pending_leave_list || []).length === 0 ? (
          <div style={{ color:'#94a3b8', fontSize:13, padding:'8px 0' }}>Không có đơn nào cần duyệt</div>
        ) : (
          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
              <thead>
                <tr style={{ background:'#f8fafc' }}>
                  {['Nhân viên','Phòng ban','Loại nghỉ','Từ ngày','Đến ngày','Số ngày','Lý do'].map(h => (
                    <th key={h} style={{ padding:'9px 12px', textAlign:'left', fontSize:11, fontWeight:700, color:'#64748b', whiteSpace:'nowrap', borderBottom:'2px solid #f1f5f9' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.pending_leave_list.map(req => (
                  <tr key={req.id}
                    onMouseEnter={e => e.currentTarget.style.background='#fafafa'}
                    onMouseLeave={e => e.currentTarget.style.background=''}>
                    <td style={{ padding:'9px 12px', fontWeight:600, borderBottom:'1px solid #f8fafc' }}>{req.employee_name}</td>
                    <td style={{ padding:'9px 12px', color:'#64748b', borderBottom:'1px solid #f8fafc' }}>{req.department_name}</td>
                    <td style={{ padding:'9px 12px', borderBottom:'1px solid #f8fafc' }}>
                      <span style={{ background:'#eff6ff', color:'#1d4ed8', padding:'2px 8px', borderRadius:10, fontSize:11, fontWeight:600 }}>
                        {LEAVE_TYPE[req.leave_type] || req.leave_type}
                      </span>
                    </td>
                    <td style={{ padding:'9px 12px', borderBottom:'1px solid #f8fafc', whiteSpace:'nowrap' }}>{fmtDate(req.from_date)}</td>
                    <td style={{ padding:'9px 12px', borderBottom:'1px solid #f8fafc', whiteSpace:'nowrap' }}>{fmtDate(req.to_date)}</td>
                    <td style={{ padding:'9px 12px', textAlign:'center', fontWeight:700, borderBottom:'1px solid #f8fafc' }}>{req.days}n</td>
                    <td style={{ padding:'9px 12px', color:'#64748b', borderBottom:'1px solid #f8fafc', maxWidth:200,
                      whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{req.reason || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      {/* ── ROW 6: Quick links (admin) ── */}
      {isAdmin && (
        <Section title="⚡ Truy cập nhanh">
          <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
            {[
              ['👥 Nhân viên','/employees','#1e3a8a'],
              ['🏬 Phòng ban','/departments','#6366f1'],
              ['💼 Chức danh','/positions','#8b5cf6'],
              ['⏱️ Chấm công','/attendance','#0891b2'],
              ['📅 Nghỉ phép','/leave','#f59e0b'],
              ['💰 Bảng lương','/payroll','#22c55e'],
              ['🎯 Chiến dịch','/campaigns','#0891b2'],
              ['📋 Nhiệm vụ','/tasks','#6366f1'],
              ['📈 Báo cáo','/reports','#0891b2'],
              ['🔑 Phân quyền','/users','#dc2626'],
            ].map(([label, path, color]) => (
              <button key={path} onClick={() => navigate(path)} style={{
                padding:'8px 16px', borderRadius:8, border:`1.5px solid ${color}20`,
                background:`${color}08`, color, cursor:'pointer', fontSize:13, fontWeight:600,
                transition:'all 0.15s',
              }}
                onMouseEnter={e => { e.currentTarget.style.background=`${color}18`; }}
                onMouseLeave={e => { e.currentTarget.style.background=`${color}08`; }}>
                {label}
              </button>
            ))}
          </div>
        </Section>
      )}
    </div>
  );
}
